import React, { useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { FormDialog, InputField, ActionButton } from "../v3";
import { HumanResourceService } from "../../services/hr.service";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AddEditParticularProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (particular?: Particular) => void;
  workerId: number | string | undefined;
  editId?: number | null;
  initialData?: ParticularForm | null;
}

interface Particular {
  id?: number;
  particularId?: number;
  name: string;
  defaultRate: number;
  workerId?: number;
}

interface ParticularForm {
  name: string;
  defaultRate: string;
}

interface FormErrors {
  name?: string;
  defaultRate?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const emptyForm: ParticularForm = { name: "", defaultRate: "" };

export const AddEditParticular: React.FC<AddEditParticularProps> = ({
  open,
  onClose,
  onSuccess,
  workerId,
  editId,
  initialData,
}) => {
  const [form, setForm] = useState<ParticularForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  React.useEffect(() => {
    if (open) {
      setForm(initialData || emptyForm);
      setFormErrors({});
    }
  }, [open, initialData]);

  const validateForm = (): boolean => {
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = "Item name is required";
    const rate = Number(form.defaultRate);
    if (!form.defaultRate || isNaN(rate) || rate <= 0) errs.defaultRate = "Enter a positive rate";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) return;
    if (!workerId && !editId) {
      toast.error("Worker ID is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        defaultRate: Number(form.defaultRate),
        workerId: Number(workerId),
      };

      if (editId) {
        await HumanResourceService.updateWorkerParticular(editId, {
          name: payload.name,
          defaultRate: payload.defaultRate,
        });
        toast.success("Particular updated successfully");
        onSuccess();
      } else {
        const res = await HumanResourceService.createWorkerParticular(payload);
        toast.success("Particular added successfully");

        // Prefer the id from the API response; fall back to payload fields
        const returned: Particular = res?.data ?? { ...payload };
        onSuccess(returned);
      }

      setForm(emptyForm);
      onClose();
    } catch (error) {
      console.error("Error saving particular:", error);
      toast.error("Error saving particular");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={editId ? "Edit Particular" : "Add Particular"}
      onClose={onClose}
      onSubmit={handleSave}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={onClose} variant="outlined" />
          <ActionButton buttonText={editId ? "Update" : "Save"} type="submit" loading={saving} />
        </>
      }
    >
      <Box mt={1} display="flex" flexDirection="column" gap={2} minWidth={340}>
        <InputField
          required
          label="Item Name"
          name="name"
          placeholder="e.g. Brick"
          value={form.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, name: e.target.value }))}
          error={!!formErrors.name}
          helperText={formErrors.name}
          fullWidth
        />
        <InputField
          required
          label="Default Rate (₹)"
          name="defaultRate"
          type="number"
          placeholder="0.00"
          value={form.defaultRate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, defaultRate: e.target.value }))}
          error={!!formErrors.defaultRate}
          helperText={formErrors.defaultRate}
          fullWidth
        />
      </Box>
    </FormDialog>
  );
};
